import type {
  FormDocument,
  FormFieldDef,
  FormSectionDef,
} from "@los-workspace/workflow-runtime";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readStaticDefault(value: unknown): unknown {
  if (!isRecord(value)) {
    return value;
  }
  if (value["type"] === "static") {
    return value["value"];
  }
  // v2 ValueSource defaults (apiResponse, expression, …) are resolved by the
  // form-builder runtime — not as raw control values in the LOS pack form.
  return undefined;
}

function mapKindToFieldType(
  kind: string,
  typeId: string,
  config: Record<string, unknown>,
): string {
  const normalizedKind = kind.toLowerCase();
  const normalizedTypeId = typeId.toLowerCase();

  if (normalizedTypeId === "radiogroup" || normalizedTypeId === "radio") {
    return "RADIO";
  }
  if (normalizedTypeId === "phone" || config["preset"] === "phone") {
    return "PHONE";
  }
  if (normalizedTypeId === "email" || config["preset"] === "email") {
    return "EMAIL";
  }
  if (normalizedTypeId === "longtext") {
    return "TEXTAREA";
  }
  if (normalizedTypeId === "radiogroup") {
    return "RADIO";
  }
  if (normalizedTypeId === "dropdown" || normalizedKind === "select") {
    return "DROPDOWN";
  }
  if (
    normalizedTypeId === "currency" ||
    config["format"] === "currency" ||
    config["preset"] === "currency"
  ) {
    return "CURRENCY";
  }
  if (
    normalizedTypeId === "date" ||
    normalizedTypeId === "datepicker" ||
    normalizedTypeId === "time" ||
    normalizedTypeId === "datetime"
  ) {
    return "DATE";
  }
  if (normalizedKind === "file" || normalizedTypeId === "fileupload") {
    return "FILE_UPLOAD";
  }
  if (normalizedKind === "boolean" || normalizedTypeId === "toggle") {
    return "BOOLEAN";
  }
  if (normalizedTypeId === "percentage" || config["preset"] === "percentage") {
    return "PERCENTAGE";
  }
  if (normalizedKind === "number") {
    return "NUMBER";
  }
  if (normalizedKind === "hidden" || normalizedTypeId === "hiddenvalue") {
    return "HIDDEN";
  }
  return "TEXT";
}

type PackQueryItem = { key?: string; value?: string; enabled?: boolean };

function mergePackQueryItems(primary: unknown, fallback: unknown): unknown {
  const primaryItems = Array.isArray(primary)
    ? (primary as PackQueryItem[])
    : [];
  const fallbackItems = Array.isArray(fallback)
    ? (fallback as PackQueryItem[])
    : [];
  if (!fallbackItems.length) {
    return primaryItems.length ? primary : undefined;
  }
  if (!primaryItems.length) {
    return fallback;
  }
  const seen = new Set(
    primaryItems.map((item) => item?.key).filter((key): key is string => !!key),
  );
  const merged = [...primaryItems];
  for (const item of fallbackItems) {
    if (item?.key && !seen.has(item.key)) {
      merged.push(item);
      seen.add(item.key);
    }
  }
  return merged;
}

function mergeDependsOnIntoOptionsConfig(
  existing: Record<string, unknown>,
  config: Record<string, unknown>,
): void {
  const configDepends = config["dependsOn"];
  if (!Array.isArray(configDepends) || !configDepends.length) {
    return;
  }
  const prior = Array.isArray(existing["dependsOn"])
    ? (existing["dependsOn"] as string[])
    : [];
  existing["dependsOn"] = [...new Set([...prior, ...configDepends])];
}

function isApiSelectOptionsConfig(
  existing: Record<string, unknown>,
  config: Record<string, unknown>,
): boolean {
  const endpoint =
    readString(existing["endpoint"]) ?? readString(config["endpoint"]);
  return (
    readString(existing["source"]) === "api" ||
    readString(config["source"]) === "api" ||
    !!endpoint ||
    !!readString(config["dataSourceId"]) ||
    !!readString(existing["dataSourceId"])
  );
}

/** Super-admin v2 exports sometimes include placeholder static `config.options` with API endpoint. */
function mergeApiSelectOptionsConfig(
  existing: Record<string, unknown>,
  config: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const mergedQuery = mergePackQueryItems(existing["query"], config["query"]);
  if (mergedQuery) {
    existing["query"] = mergedQuery;
  }
  if (!readString(existing["endpoint"]) && readString(config["endpoint"])) {
    existing["endpoint"] = config["endpoint"];
  }
  if (!readString(existing["method"]) && readString(config["method"])) {
    existing["method"] = config["method"];
  }
  if (!readString(existing["source"])) {
    existing["source"] = "api";
  }
  const optionsMapping = isRecord(existing["responseMapping"])
    ? existing["responseMapping"]
    : null;
  const configMapping = isRecord(config["responseMapping"])
    ? config["responseMapping"]
    : null;
  if (
    configMapping &&
    (!optionsMapping || !Object.keys(optionsMapping).length)
  ) {
    existing["responseMapping"] = configMapping;
  }
  return Object.keys(existing).length ? existing : undefined;
}

function mergeStaticSelectOptionsConfig(
  existing: Record<string, unknown>,
  config: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const options = Array.isArray(existing["options"])
    ? existing["options"]
    : config["options"];
  if (!Array.isArray(options)) {
    return Object.keys(existing).length ? existing : undefined;
  }
  return {
    ...existing,
    source: readString(existing["source"]) ?? "static",
    options,
  };
}

function mergeOptionsConfig(config: Record<string, unknown>): unknown {
  const existing = isRecord(config["optionsConfig"])
    ? { ...config["optionsConfig"] }
    : {};

  mergeDependsOnIntoOptionsConfig(existing, config);

  if (isApiSelectOptionsConfig(existing, config)) {
    return mergeApiSelectOptionsConfig(existing, config);
  }
  return mergeStaticSelectOptionsConfig(existing, config);
}

function mergeUiHints(
  element: Record<string, unknown>,
  config: Record<string, unknown>,
): Record<string, unknown> {
  const hints = isRecord(config["uiHints"]) ? { ...config["uiHints"] } : {};
  const elementHints = element["uiHints"];
  if (isRecord(elementHints)) {
    Object.assign(hints, elementHints);
  }
  const helpText =
    readString(hints["helpText"]) ?? readString(element["helpText"]);
  if (helpText) {
    hints["helpText"] = helpText;
  }
  if (config["accept"] && !hints["accept"]) {
    hints["accept"] = config["accept"];
  }
  if (config["kind"] === "file" && !hints["title"]) {
    hints["title"] = readString(element["label"]);
  }
  const orientation = readString(config["orientation"]);
  if (orientation && !hints["orientation"]) {
    hints["orientation"] = orientation;
  }
  const typeId = readString(element["typeId"])?.toLowerCase() ?? "";
  if (typeId === "time") {
    hints["timeOnly"] = true;
  }
  if (typeId === "datetime" && hints["enableTime"] !== true) {
    hints["enableTime"] = true;
  }
  return hints;
}

function mapValidationEntry(item: unknown): unknown {
  if (!isRecord(item)) {
    return item;
  }
  return {
    ...item,
    type: readString(item["type"]) ?? readString(item["operator"]),
  };
}

function mergeValidationArrays(...sources: unknown[][]): unknown[] | undefined {
  const merged = new Map<string, unknown>();
  const remember = (item: unknown, index: number) => {
    if (!isRecord(item)) {
      merged.set(`raw:${index}:${merged.size}`, item);
      return;
    }
    const key =
      readString(item["id"]) ??
      `${readString(item["type"]) ?? readString(item["operator"]) ?? "rule"}:${index}`;
    if (!merged.has(key)) {
      merged.set(key, mapValidationEntry(item));
    }
  };
  for (const source of sources) {
    source.forEach(remember);
  }
  const values = [...merged.values()];
  return values.length ? values : undefined;
}

function mapValidations(
  element: Record<string, unknown>,
  config: Record<string, unknown>,
): unknown[] | undefined {
  const fromElementValidation = Array.isArray(element["validation"])
    ? element["validation"]
    : [];
  const fromElementList = Array.isArray(element["validations"])
    ? element["validations"]
    : [];
  const fromConfigList = Array.isArray(config["validations"])
    ? config["validations"]
    : [];
  const fromValidationConfig =
    isRecord(config["validationConfig"]) &&
    Array.isArray(config["validationConfig"]["validations"])
      ? (config["validationConfig"]["validations"] as unknown[])
      : [];

  return mergeValidationArrays(
    fromElementValidation,
    fromElementList,
    fromConfigList,
    fromValidationConfig,
  );
}

function resolveElementValidationConfig(
  element: Record<string, unknown>,
  config: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (isRecord(config["validationConfig"])) {
    return config["validationConfig"];
  }
  if (isRecord(element["validationConfig"])) {
    return element["validationConfig"];
  }
  return undefined;
}

function mergeValidationRulesBags(
  ...bags: Array<Record<string, unknown> | undefined>
): Record<string, unknown> | undefined {
  const merged: Record<string, unknown> = {};
  for (const bag of bags) {
    if (!bag) continue;
    Object.assign(merged, bag);
  }
  return Object.keys(merged).length ? merged : undefined;
}

function elementToField(
  element: Record<string, unknown>,
  order: number,
): FormFieldDef | null {
  const binding = readString(element["binding"]);
  if (!binding) {
    return null;
  }

  const config = (element["config"] ??
    element["configuration"] ??
    {}) as Record<string, unknown>;
  const state = isRecord(element["state"]) ? element["state"] : {};
  const kind = readString(element["kind"]) ?? "";
  const typeId = readString(element["typeId"]) ?? "";
  const layout = isRecord(element["layout"]) ? { ...element["layout"] } : {};

  return {
    id: readString(element["id"]) ?? binding,
    fieldCode: binding,
    fieldType: mapKindToFieldType(kind, typeId, {
      ...config,
      preset: element["preset"],
    }),
    dataType: readString(config["dataType"]) ?? "STRING",
    label: readString(element["label"]) ?? binding,
    order,
    placeholder:
      readString(config["placeholder"]) ??
      readString(element["placeholder"]) ??
      undefined,
    defaultValue: readStaticDefault(element["defaultValue"]),
    readOnly: state["readOnly"] === true,
    required: element["required"] === true || state["required"] === true,
    visibility:
      state["hidden"] === true ||
      kind.toLowerCase() === "hidden" ||
      typeId.toLowerCase() === "hiddenvalue"
        ? "HIDDEN"
        : "VISIBLE",
    visibleWhen: state["visibleWhen"],
    requiredWhen: state["requiredWhen"],
    optionsConfig: mergeOptionsConfig(config),
    uiHints: mergeUiHints(element, config),
    validationRules: mergeValidationRulesBags(
      isRecord(element["validationRules"])
        ? element["validationRules"]
        : undefined,
      isRecord(config["validationRules"])
        ? config["validationRules"]
        : undefined,
    ) as FormFieldDef["validationRules"],
    validations: mapValidations(element, config),
    validationConfig: resolveElementValidationConfig(element, config),
    verificationConfig: isRecord(config["verificationConfig"])
      ? config["verificationConfig"]
      : undefined,
    calculationConfig: isRecord(config["calculationConfig"])
      ? config["calculationConfig"]
      : undefined,
    layout,
  } as FormFieldDef;
}

function mapLegacyReadonlySource(
  source: Record<string, unknown>,
): Record<string, unknown> {
  if (source["type"] === "expression" && source["expression"]) {
    return { type: "formula", formula: source["expression"] };
  }
  if (source["type"] === "formula" && source["formula"]) {
    return { type: "formula", formula: source["formula"] };
  }
  if (source["endpoint"] || source["type"] === "api") {
    return { ...source, type: "api" };
  }
  if (source["type"] === "formContext" && source["path"]) {
    return { type: "field", fieldCode: source["path"] };
  }
  return source;
}

function readonlyValueElement(
  element: Record<string, unknown>,
  order: number,
): Record<string, unknown> | null {
  const kind = (readString(element["kind"]) ?? "").toLowerCase();
  const typeId = (readString(element["typeId"]) ?? "").toLowerCase();
  if (typeId !== "readonlyvalue" && kind !== "content") {
    return null;
  }
  if (typeId !== "readonlyvalue") {
    return null;
  }

  const config = (element["config"] ??
    element["configuration"] ??
    {}) as Record<string, unknown>;
  const source = isRecord(config["source"]) ? config["source"] : {};

  const explicitOrder = Number(element["order"]);
  return {
    id: readString(element["id"]),
    type: "READONLY_VALUE",
    order: Number.isFinite(explicitOrder) ? explicitOrder : order,
    configuration: {
      label: readString(config["label"]) ?? readString(element["label"]),
      format: config["format"],
      source: mapLegacyReadonlySource(source),
    },
  };
}

function contentElement(
  element: Record<string, unknown>,
  order: number,
): Record<string, unknown> | null {
  const kind = readString(element["kind"]) ?? "";
  const typeId = readString(element["typeId"]) ?? "";
  const config = (element["config"] ??
    element["configuration"] ??
    {}) as Record<string, unknown>;
  if (typeId.toLowerCase() === "heading") {
    return {
      id: readString(element["id"]),
      type: "HEADING",
      order,
      configuration: {
        align: config["align"] ?? "start",
        level: config["level"] ?? 4,
        text: readString(config["text"]) ?? "",
      },
    };
  }
  if (kind !== "content" && typeId !== "text") {
    return null;
  }
  if (readString(element["binding"])) {
    return null;
  }
  return {
    id: readString(element["id"]),
    type: "TEXT",
    order,
    configuration: {
      align: config["align"] ?? "start",
      text: readString(config["text"]) ?? "",
    },
  };
}

function actionElement(
  element: Record<string, unknown>,
  order: number,
): Record<string, unknown> | null {
  const kind = readString(element["kind"]) ?? "";
  const typeId = readString(element["typeId"]) ?? "";
  if (kind !== "action" && typeId !== "button") {
    return null;
  }
  const config = (element["config"] ??
    element["configuration"] ??
    {}) as Record<string, unknown>;
  return {
    id: readString(element["id"]),
    type: "BUTTON",
    order,
    configuration: { ...config },
    config,
  };
}

function isSubmitAction(element: Record<string, unknown>): boolean {
  const config = (element["config"] ??
    element["configuration"] ??
    {}) as Record<string, unknown>;
  const action = (readString(config["action"]) ?? "").toUpperCase();
  const actionType = (readString(config["actionType"]) ?? "").toLowerCase();
  return action === "SUBMIT" || actionType === "submit";
}

function splitPageElements(elements: Record<string, unknown>[]): {
  fields: FormFieldDef[];
  displayElements: Record<string, unknown>[];
  footerElements: Record<string, unknown>[];
} {
  const fields: FormFieldDef[] = [];
  const displayElements: Record<string, unknown>[] = [];
  const footerElements: Record<string, unknown>[] = [];

  elements.forEach((element, elementIndex) => {
    const field = elementToField(element, elementIndex);
    if (field) {
      fields.push(field);
      return;
    }
    const action = actionElement(element, elementIndex);
    if (action) {
      if (isSubmitAction(element)) {
        footerElements.push(action);
      } else {
        displayElements.push(action);
      }
      return;
    }
    const readonly = readonlyValueElement(element, elementIndex);
    if (readonly) {
      displayElements.push(readonly);
      return;
    }
    const content = contentElement(element, elementIndex);
    if (content) {
      displayElements.push(content);
    }
  });

  return { fields, displayElements, footerElements };
}

function walkMultiColumnSlots(
  slots: unknown[],
  visit: (nodes: Record<string, unknown>[]) => void,
): boolean {
  const columns = slots
    .filter((slot): slot is unknown[] => Array.isArray(slot))
    .map((slot) => slot.filter(isRecord));
  if (columns.length < 2 || !columns.some((column) => column.length > 0)) {
    return false;
  }
  const maxLen = Math.max(...columns.map((column) => column.length));
  for (let index = 0; index < maxLen; index += 1) {
    for (const column of columns) {
      const item = column[index];
      if (item) {
        visit([item]);
      }
    }
  }
  return true;
}

function walkNestedSlotArrays(
  slots: unknown[],
  visit: (nodes: Record<string, unknown>[]) => void,
): boolean {
  let hasNested = false;
  for (const slot of slots) {
    if (Array.isArray(slot) && slot.length) {
      hasNested = true;
      visit(slot.filter(isRecord));
    }
  }
  return hasNested;
}

function flattenNestedSectionElements(
  elements: Record<string, unknown>[],
): Record<string, unknown>[] {
  const flattened: Record<string, unknown>[] = [];

  const visit = (nodes: Record<string, unknown>[]): void => {
    for (const node of nodes) {
      const slots = node["slots"];
      if (Array.isArray(slots) && slots.length >= 2) {
        if (walkMultiColumnSlots(slots, visit)) {
          continue;
        }
      }
      if (Array.isArray(slots) && walkNestedSlotArrays(slots, visit)) {
        continue;
      }
      flattened.push(node);
    }
  };

  visit(elements);
  return flattened;
}

function readSectionColumnCount(elements: Record<string, unknown>[]): number {
  for (const element of elements) {
    const config = element["config"];
    if (isRecord(config) && Number(config["columnCount"]) >= 2) {
      return Number(config["columnCount"]);
    }
    const typeId = readString(element["typeId"]);
    if (typeId === "columns" && isRecord(config) && config["columnCount"]) {
      return Number(config["columnCount"]);
    }
  }
  return 1;
}

function pageSectionToFormSection(
  section: Record<string, unknown>,
  pageId: string,
  sectionIndex: number,
  pageVisibleWhen: unknown,
): FormSectionDef {
  const elements = Array.isArray(section["elements"])
    ? section["elements"].filter(isRecord)
    : [];
  const flatElements = flattenNestedSectionElements(elements);
  const { fields, displayElements, footerElements } =
    splitPageElements(flatElements);
  const columns = Number(
    section["columns"] ?? readSectionColumnCount(elements) ?? 1,
  );
  const existingLayout = isRecord(section["layout"]) ? section["layout"] : {};
  const hints = isRecord(section["uiHints"]) ? section["uiHints"] : {};

  return {
    ...section,
    id: readString(section["id"]) ?? `${pageId}-section-${sectionIndex}`,
    title: readString(section["title"]) ?? "",
    order: Number(section["order"] ?? sectionIndex),
    stepId: pageId,
    layout: { ...existingLayout, columns },
    uiHints: hints,
    fields,
    elements: displayElements,
    footer: footerElements.length
      ? { elements: footerElements }
      : section["footer"],
    visibleWhen: pageVisibleWhen ?? section["visibleWhen"],
  };
}

/**
 * Convert schema v2 page/element forms into the Opportunity FormDocument
 * shape (steps + sections + fields) used by loan-tab bridges.
 */
export function normalizePageFormDocument(
  raw: FormDocument | null,
): FormDocument | null {
  if (!raw) {
    return null;
  }

  const formCode =
    readString(raw.formCode) ?? readString(raw["code"]) ?? "UNKNOWN_FORM";
  const title = readString(raw["title"]) ?? readString(raw["name"]) ?? formCode;
  const pages = raw["pages"];

  if (!Array.isArray(pages) || pages.length === 0) {
    return { ...raw, formCode, title };
  }

  const steps: Array<Record<string, unknown>> = [];
  const sections: FormSectionDef[] = [];

  const sortedPages = pages
    .filter(isRecord)
    .slice()
    .sort((a, b) => Number(a["order"] ?? 0) - Number(b["order"] ?? 0));

  for (const [pageIndex, page] of sortedPages.entries()) {
    const pageId = readString(page["id"]) ?? `page-${pageIndex}`;
    steps.push({
      id: pageId,
      title: readString(page["title"]) ?? `Step ${pageIndex + 1}`,
      order: Number(page["order"] ?? pageIndex),
      visibleWhen: page["visibleWhen"],
    });

    const pageSections = Array.isArray(page["sections"])
      ? page["sections"].filter(isRecord)
      : [];

    for (const [sectionIndex, section] of pageSections.entries()) {
      sections.push(
        pageSectionToFormSection(
          section,
          pageId,
          sectionIndex,
          page["visibleWhen"],
        ),
      );
    }
  }

  return {
    ...raw,
    formCode,
    title,
    steps,
    sections,
  };
}
